import { faCaretDown, faCaretRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Folder } from '@workspace/view-model-folder/domain/Folder';
import { KataSearchViewModelItem } from './KataSearchViewModelItem';
import { useState, useCallback, useEffect, useMemo } from 'react';
import type { Workspace } from '@workspace/domain/workspace';
import { ViewModelEntity } from '@view-model/domain/view-model';
import { useAtomValue } from 'jotai';
import { filteredKataSearchRecordsAtom } from './kataSearchResultState';
import type { RootFolderTree } from '@workspace/view-model-folder/domain/FolderTree';
import { FolderItemTitle } from '../../items';

type FolderItemProps = {
  workspace: Workspace;
  rootFolderTree: RootFolderTree;
  folder: Folder;
  searchKeyword: string;
};

export const KataSearchFolderItem: React.FC<FolderItemProps> = ({
  workspace,
  rootFolderTree,
  folder,
  searchKeyword,
}: FolderItemProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const { viewModels, folders } = useAtomValue(filteredKataSearchRecordsAtom);

  const toggleFolder = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const sortedFolders = useMemo(() => {
    return rootFolderTree
      .getFolderIdsOf(folder.id)
      .map((fid) => folders[fid])
      .filter((folder) => !!folder)
      .sort(Folder.compare);
  }, [rootFolderTree, folders, folder.id]);

  const sortedViewModels = useMemo(() => {
    return rootFolderTree
      .getViewModelIdsOf(folder.id)
      .map((viewModelId) => viewModels[viewModelId])
      .filter((viewModel) => !!viewModel)
      .sort(ViewModelEntity.compare);
  }, [rootFolderTree, viewModels, folder.id]);

  useEffect(() => {
    if (searchKeyword) {
      setIsOpen(true);
    }
  }, [searchKeyword]);

  return (
    <div>
      <div className="flex items-center">
        <button className="mr-1" onClick={toggleFolder}>
          <FontAwesomeIcon icon={isOpen ? faCaretDown : faCaretRight} className="size-4" />
        </button>
        <div className="grow overflow-hidden rounded-sm hover:bg-gray-300">
          <FolderItemTitle workspace={workspace} folder={folder} searchKeyword={searchKeyword} isExternal />
        </div>
      </div>

      {isOpen && (
        <div className="ml-4">
          {sortedFolders.map((folder) => (
            <KataSearchFolderItem
              key={folder.id}
              workspace={workspace}
              rootFolderTree={rootFolderTree}
              folder={folder}
              searchKeyword={searchKeyword}
            />
          ))}
          {sortedViewModels.map((viewModel) => (
            <KataSearchViewModelItem
              key={viewModel.id}
              viewModel={viewModel}
              workspace={workspace}
              searchKeyword={searchKeyword}
            />
          ))}
        </div>
      )}
    </div>
  );
};
